import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy,
  limit,
  Timestamp,
  onSnapshot,
  DocumentData,
  QuerySnapshot
} from 'firebase/firestore';

const firebaseConfig = {
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

export interface PremiumStatus {
  platform: 'Android' | 'iOS' | 'macOS' | 'Web';
  renewalDate: string;
  status: boolean;
  updatedAt: Timestamp | Date;
  userId: string | null;
  uuid: string;
  /** Store plan the subscriber is on: 'Standard Yearly' | 'Standard' | 'Premium Monthly' | 'Basic'. */
  planName?: string | null;
  email?: string;
  name?: string;
  lastEmailSentAt?: string | null;
  lastEmailType?: 'feedback' | 'winback' | null;
  /** Firestore document id. Not always equal to `uuid` — see dedupePremiumStatuses. */
  docId?: string;
  /** How many premium_status documents were merged into this row (1 = no duplicates). */
  docCount?: number;
  /** Document ids that were merged, newest first. */
  mergedDocIds?: string[];
}

export interface PlanBreakdown {
  /** Raw plan name, e.g. 'Standard Yearly'. */
  plan: string;
  tier: string;
  cycle: BillingCycle;
  count: number;
  activeCount: number;
}

export interface PremiumStatusMetrics {
  totalPremiumUsers: number;
  activeUsers: number;
  expiredUsers: number;
  platformDistribution: {
    platform: string;
    count: number;
    activeCount: number;
  }[];
  renewalTimeline: {
    date: string;
    count: number;
  }[];
  planDistribution: PlanBreakdown[];
  recentUpdates: PremiumStatus[];
}

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

/**
 * `updatedAt` reaches us in three shapes: a Firestore client Timestamp (direct
 * reads), a plain Date, or the admin SDK's serialised `{_seconds}` form (the
 * /dashboard/subscribers API). Normalise all of them to epoch millis.
 */
export const premiumUpdatedAtMillis = (value: unknown): number => {
  if (!value) return 0;
  if (value instanceof Timestamp) return value.toMillis();
  if (value instanceof Date) return value.getTime();
  const obj = value as { seconds?: number; _seconds?: number };
  if (typeof obj.seconds === 'number') return obj.seconds * 1000;
  if (typeof obj._seconds === 'number') return obj._seconds * 1000;
  const parsed = new Date(value as string).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const formatPremiumUpdatedAt = (value: unknown): string => {
  const millis = premiumUpdatedAtMillis(value);
  return millis ? new Date(millis).toLocaleString() : '\u2014';
};

export type BillingCycle = 'Monthly' | 'Yearly' | 'Unknown';

export interface PlanDetails {
  /** Tier without the billing word: 'Standard', 'Basic', 'Premium', 'Unknown'. */
  tier: string;
  cycle: BillingCycle;
  /** The raw planName, or 'Unknown' when the record predates plan tracking. */
  label: string;
}

/**
 * `planName` is written by the app from the store entitlement
 * (notely-app auth_provider.dart): premium_yearly_2 -> 'Standard Yearly',
 * premium_monthly_2 -> 'Standard', anything else paid -> 'Premium Monthly',
 * and promo redemptions land on 'Basic'. Every SKU except the *_yearly_* one is
 * a 30-day subscription, so a known tier with no period word is monthly; only a
 * missing or explicitly unknown plan is reported as an unknown cycle.
 */
const SKU_PLAN_NAMES: Record<string, string> = {
  premium_monthly_1: 'Basic',
  premium_monthly_2: 'Standard',
  premium_yearly_2: 'Standard Yearly',
};

export const describePlan = (planName?: string | null): PlanDetails => {
  // A few records stored the raw store SKU instead of the display name; map
  // those onto the same plan so they do not split the chart into two bars.
  const stored = (planName ?? '').trim();
  const raw = SKU_PLAN_NAMES[stored] ?? stored;
  if (!raw || /^unknown/i.test(raw)) return { tier: 'Unknown', cycle: 'Unknown', label: 'Unknown' };

  const cycle: BillingCycle = /year|annual/i.test(raw) ? 'Yearly' : 'Monthly';
  const tier = raw.replace(/\b(yearly|annual(ly)?|monthly)\b/gi, '').replace(/\s+/g, ' ').trim() || 'Premium';
  return { tier, cycle, label: raw };
};

/** Plan counts for the dashboard chart, busiest plan first. */
export const planDistribution = (records: PremiumStatus[]): PlanBreakdown[] => {
  const byPlan = new Map<string, PlanBreakdown>();
  records.forEach(record => {
    const { tier, cycle, label } = describePlan(record.planName);
    const entry = byPlan.get(label) ?? { plan: label, tier, cycle, count: 0, activeCount: 0 };
    entry.count++;
    if (record.status) entry.activeCount++;
    byPlan.set(label, entry);
  });
  return Array.from(byPlan.values()).sort((a, b) => b.count - a.count);
};

/**
 * The app writes premium_status under `user.uid ?? deviceUuid` (see
 * notely-app auth_provider.dart), so one person ends up with two documents once
 * they sign in: an anonymous device-keyed one and a uid-keyed one, both holding
 * the same `uuid` field. That is why the dashboard showed the same subscriber
 * twice. Collapse each `uuid` to a single row: newest document wins for the
 * subscription state, and the fields that only exist on one sibling (email,
 * name, email-outreach history) are carried over instead of being lost.
 */
export const dedupePremiumStatuses = (records: PremiumStatus[]): PremiumStatus[] => {
  const groups = new Map<string, PremiumStatus[]>();
  records.forEach(record => {
    const key = record.uuid || record.docId || '';
    const group = groups.get(key);
    if (group) group.push(record);
    else groups.set(key, [record]);
  });

  const merged = Array.from(groups.values()).map(group => {
    const ordered = [...group].sort(
      (a, b) => premiumUpdatedAtMillis(b.updatedAt) - premiumUpdatedAtMillis(a.updatedAt)
    );
    const newest = ordered[0];
    if (ordered.length === 1) return { ...newest, docCount: 1 };

    const firstPresent = (pick: (r: PremiumStatus) => string | null | undefined) =>
      ordered.map(pick).find(v => v != null && v !== '') ?? undefined;

    // Outreach history lives on whichever document the send happened to hit,
    // so take the most recent send across the whole group.
    const lastEmailed = ordered
      .filter(r => r.lastEmailSentAt)
      .sort((a, b) => new Date(b.lastEmailSentAt!).getTime() - new Date(a.lastEmailSentAt!).getTime())[0];

    return {
      ...newest,
      email: firstPresent(r => r.email),
      name: firstPresent(r => r.name),
      planName: firstPresent(r => r.planName) ?? null,
      userId: ordered.map(r => r.userId).find(Boolean) ?? null,
      lastEmailSentAt: lastEmailed?.lastEmailSentAt ?? null,
      lastEmailType: lastEmailed?.lastEmailType ?? null,
      docCount: ordered.length,
      mergedDocIds: ordered.map(r => r.docId).filter(Boolean) as string[],
    };
  });

  return merged.sort(
    (a, b) => premiumUpdatedAtMillis(b.updatedAt) - premiumUpdatedAtMillis(a.updatedAt)
  );
};

export const fetchPremiumStatusData = async (
  timePeriod: 'day' | 'week' | 'month' = 'month'
): Promise<PremiumStatusMetrics> => {
  try {
    const premiumStatusCollection = collection(db, 'premium_status');
    
    const now = new Date();
    let startDate = new Date();
    
    switch (timePeriod) {
      case 'day':
        startDate.setDate(now.getDate() - 1);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
    }
    
    const q = query(
      premiumStatusCollection,
      orderBy('updatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const allStatuses: PremiumStatus[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      allStatuses.push({
        platform: data.platform,
        renewalDate: data.renewalDate,
        status: data.status,
        updatedAt: data.updatedAt,
        userId: data.userId ?? null,
        uuid: data.uuid || doc.id,
        planName: data.planName ?? null,
        docId: doc.id,
        // The app stores these on the document itself for signed-in users; using
        // them means Recent Premium Updates is not blank while the subscribers
        // API (which also resolves legacy records through Auth) is still loading.
        email: data.email ?? undefined,
        name: data.displayName ?? undefined,
        lastEmailSentAt: data.lastEmailSentAt instanceof Timestamp
          ? data.lastEmailSentAt.toDate().toISOString()
          : (data.lastEmailSentAt ?? null),
        lastEmailType: data.lastEmailType ?? null,
      });
    });
    
    // Collapse the duplicate documents each subscriber can accumulate before
    // any counting happens, so the totals below are users, not documents.
    const uniqueStatuses = dedupePremiumStatuses(allStatuses);

    const filteredStatuses = uniqueStatuses.filter(status => {
      return premiumUpdatedAtMillis(status.updatedAt) >= startDate.getTime();
    });
    
    const activeUsers = filteredStatuses.filter(s => s.status).length;
    const expiredUsers = filteredStatuses.filter(s => !s.status).length;
    
    const platformMap = new Map<string, { count: number; activeCount: number }>();
    filteredStatuses.forEach(status => {
      const existing = platformMap.get(status.platform) || { count: 0, activeCount: 0 };
      existing.count++;
      if (status.status) {
        existing.activeCount++;
      }
      platformMap.set(status.platform, existing);
    });
    
    const platformDistribution = Array.from(platformMap.entries()).map(([platform, data]) => ({
      platform,
      count: data.count,
      activeCount: data.activeCount
    }));
    
    const renewalMap = new Map<string, number>();
    filteredStatuses.forEach(status => {
      if (status.renewalDate) {
        const renewalDate = new Date(status.renewalDate).toLocaleDateString();
        renewalMap.set(renewalDate, (renewalMap.get(renewalDate) || 0) + 1);
      }
    });
    
    const renewalTimeline = Array.from(renewalMap.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-30);
    
    return {
      totalPremiumUsers: filteredStatuses.length,
      activeUsers,
      expiredUsers,
      platformDistribution,
      renewalTimeline,
      planDistribution: planDistribution(filteredStatuses),
      recentUpdates: filteredStatuses
    };
  } catch (error) {
    console.error('Error fetching premium status data:', error);
    throw error;
  }
};

export const subscribeToPremiumStatusUpdates = (
  callback: (metrics: PremiumStatusMetrics) => void,
  timePeriod: 'day' | 'week' | 'month' = 'month'
) => {
  const premiumStatusCollection = collection(db, 'premium_status');
  
  const q = query(
    premiumStatusCollection,
    orderBy('updatedAt', 'desc'),
    limit(100)
  );
  
  return onSnapshot(q, async (snapshot: QuerySnapshot<DocumentData>) => {
    try {
      const metrics = await fetchPremiumStatusData(timePeriod);
      callback(metrics);
    } catch (error) {
      console.error('Error in premium status subscription:', error);
    }
  });
};